import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  FormQuestionDto,
  FormSectionDto,
  UpdateQuestionConfigDto,
  UserProfileDto,
  UserRole,
} from "@green0meter/api-client";

import { DOMAIN_QUESTION_DETAILS } from "../domain";

import { questionsConfigApi } from "@api/services";
import { ACTION_GET_FORM_SECTIONS, DOMAIN_FORMS, roleGuard } from "@api";

const allowedRoles = [UserRole.Owner, UserRole.Manager];

type UpdateQuestionConfigRequest = {
  questionId: string;
  update: UpdateQuestionConfigDto;
};

const updateQuestionConfigRequest = async (
  req: UpdateQuestionConfigRequest,
): Promise<FormQuestionDto> => {
  const response =
    await questionsConfigApi.questionsConfigControllerUpdateQuestionConfig(
      req.questionId,
      req.update,
    );

  return response.data;
};

export const useUpdateQuestionConfig = (
  user: UserProfileDto | null,
  formId: string,
  sectionId: string,
): {
  updateQuestionConfig: (req: UpdateQuestionConfigRequest) => void;
  updateQuestionConfigAsync: (
    req: UpdateQuestionConfigRequest,
  ) => Promise<FormQuestionDto>;
  updateQuestionConfigEnabled: boolean;
  isSuccessUpdateQuestionConfig: boolean;
  isPendingUpdateQuestionConfig: boolean;
} => {
  const enabled = roleGuard(user, allowedRoles);
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isSuccess, isPending } = useMutation({
    mutationKey: [DOMAIN_QUESTION_DETAILS],
    mutationFn: enabled ? updateQuestionConfigRequest : undefined,
    onSuccess(data) {
      const queryKey = [DOMAIN_FORMS, ACTION_GET_FORM_SECTIONS, formId];
      const currentData = queryClient.getQueryData<FormSectionDto[] | undefined>(
        queryKey,
      );

      queryClient.setQueryData(queryKey, () => {
        return currentData?.map((section) => ({
          ...section,
          formQuestions:
            section.id === sectionId
              ? section.formQuestions?.map((question) => {
                  if (question.questionId === data.questionId) {
                    return data;
                  }

                  return question;
                })
              : section.formQuestions,
        }));
      });
    },
  });

  return {
    updateQuestionConfig: mutate,
    updateQuestionConfigAsync: mutateAsync,
    updateQuestionConfigEnabled: enabled,
    isSuccessUpdateQuestionConfig: isSuccess,
    isPendingUpdateQuestionConfig: isPending,
  };
};
